import React, { useEffect } from 'react';
import { ArrowLeft, Shield, CheckCircle, AlertTriangle, Users, FileText, MessageSquare, BookOpen, Gavel, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const CommunityGuidelines = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="container mx-auto px-6 py-8">
        <Link to="/community">
          <Button className="mb-8 bg-gray-800 hover:bg-gray-700 text-white border-gray-600">
            <ArrowLeft size={20} />
            Back to Community
          </Button>
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="w-24 h-24 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <Shield size={48} className="text-turquoise-400" />
          </div>
          <h1 className="display-large mb-6">
            Community <span className="turquoise-text-gradient">Posting Guidelines</span>
          </h1>
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Welcome to our Business Community! This space is designed for meaningful discussions, 
            knowledge sharing, and professional growth. To keep the space safe and valuable for everyone, 
            we ask all members to follow these simple guidelines.
          </p>
        </div>

        {/* Main Guidelines */}
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* Section 1: Posting Policy */}
          <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-turquoise-500/20 rounded-lg flex items-center justify-center">
                <FileText size={32} className="text-turquoise-400" />
              </div>
              <div>
                <h2 className="heading-2 text-white">1. Posting Policy</h2>
                <p className="text-gray-400">Guidelines for creating valuable content</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-2">Keep it Professional</h4>
                    <p className="text-gray-300 text-sm">Share insights, experiences, questions, or updates that relate to business, consultancy, and professional development.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-2">Be Respectful</h4>
                    <p className="text-gray-300 text-sm">Write with courtesy. Critique ideas, not people.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-2">Share Original Content</h4>
                    <p className="text-gray-300 text-sm">Post only what you own, or what you have permission to share. Always give credit where it's due.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-2">Stay Compliant</h4>
                    <p className="text-gray-300 text-sm">Please follow Indian laws and government policies when posting.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-yellow-400 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-yellow-400 mb-2">No Clutter Policy</h4>
                  <p className="text-gray-300 text-sm">Avoid spamming, irrelevant promotions, or repeated posts. If you'd like to promote your business or event, get prior approval from the admin team.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Content Restrictions */}
          <div className="bg-gray-900/50 border border-red-500/20 rounded-16 p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-red-500/20 rounded-lg flex items-center justify-center">
                <Gavel size={32} className="text-red-400" />
              </div>
              <div>
                <h2 className="heading-2 text-white">2. Content Restrictions</h2>
                <p className="text-gray-400">Content that is not allowed in our community</p>
              </div>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-red-400 mb-4">We don't allow content that:</h4>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                "Breaks the law or goes against government rules",
                "Promotes hate, discrimination, or personal attacks",
                "Contains vulgar, obscene, or offensive language or material",
                "Spreads false or misleading information",
                "Pushes political or religious propaganda",
                "Shares personal or confidential information without consent",
                "Contains spam, scams, or unsafe links"
              ].map((restriction, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-red-500/5 rounded-lg">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300 text-sm">{restriction}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: Our Rights */}
          <div className="bg-gray-900/50 border border-blue-500/20 rounded-16 p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Users size={32} className="text-blue-400" />
              </div>
              <div>
                <h2 className="heading-2 text-white">3. Our Rights</h2>
                <p className="text-gray-400">Community moderation and enforcement</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-gray-300">
                  Our team may remove posts or limit access if content breaks these rules. 
                  Repeated violations may lead to account suspension.
                </p>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-gray-800/50 rounded-lg">
                <MessageSquare size={20} className="text-turquoise-400 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-white mb-2">Fair Enforcement</h4>
                  <p className="text-gray-300 text-sm">
                    We believe in fair and transparent moderation. If you believe content was removed in error, 
                    please contact our admin team for review.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Community Values */}
          <div className="bg-gradient-to-r from-turquoise-500/10 to-blue-500/10 border border-turquoise-500/20 rounded-16 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart size={32} className="text-turquoise-400" />
              </div>
              <h3 className="heading-3 text-white mb-4">Building a Better Community Together</h3>
              <p className="text-gray-300 mb-6">
                These guidelines exist to create a space where professionals can learn, grow, and succeed together. 
                By following them, you help make our community a valuable resource for everyone.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/community">
                  <Button className="btn-primary-enhanced">
                    <BookOpen size={20} />
                    Join the Discussion
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-secondary">
                    Contact Admin Team
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Quick Reference */}
          <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-8">
            <h3 className="heading-3 text-white mb-6 text-center">Quick Reference</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <CheckCircle size={24} className="text-green-400 mx-auto mb-2" />
                <h4 className="font-semibold text-green-400 mb-2">Do</h4>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>Share professional insights</li>
                  <li>Ask thoughtful questions</li>
                  <li>Give credit to sources</li>
                  <li>Engage respectfully</li>
                </ul>
              </div>
              
              <div className="p-4">
                <AlertTriangle size={24} className="text-yellow-400 mx-auto mb-2" />
                <h4 className="font-semibold text-yellow-400 mb-2">Avoid</h4>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>Repeated promotions</li>
                  <li>Off-topic content</li>
                  <li>Duplicate posts</li>
                  <li>Excessive self-promotion</li>
                </ul>
              </div>
              
              <div className="p-4">
                <Gavel size={24} className="text-red-400 mx-auto mb-2" />
                <h4 className="font-semibold text-red-400 mb-2">Never</h4>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>Attack individuals</li>
                  <li>Share false information</li>
                  <li>Post inappropriate content</li>
                  <li>Violate privacy</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelines;